import type { Editor } from '@tiptap/react';
import type { DynVarOption } from '../hooks/useDynamicVariables';

function escapeHTML(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(str: string): string {
  return escapeHTML(str).replace(/"/g, '&quot;');
}

/**
 * Convert raw agent instructions (e.g. "...shipment {Tracking.ID} is...")
 * into HTML TipTap can render, turning any `{token}` that matches a known
 * dynamic variable into a mention chip showing its human-readable label.
 * Unrecognized `{...}` tokens are left as plain escaped text.
 */
export function agentInstructionsToHTML(
  text: string,
  dynVarOptions: DynVarOption[]
): string {
  if (!text) return '';

  const byValue = new Map(dynVarOptions.map((o) => [o.value, o]));
  const regex = /\{([^}]+)\}/g;

  let lastIndex = 0;
  let result = '';
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    result += escapeHTML(text.slice(lastIndex, match.index));

    const option = byValue.get(match[1]);
    if (option) {
      result +=
        `<span data-type="mention" data-id="${escapeAttr(option.value)}"` +
        ` data-label="${escapeAttr(option.label)}" title="${escapeAttr(option.label)}">` +
        `${escapeHTML(option.label)}</span>`;
    } else {
      result += escapeHTML(match[0]);
    }

    lastIndex = regex.lastIndex;
  }
  result += escapeHTML(text.slice(lastIndex));

  return `<p>${result.replace(/\n/g, '<br>')}</p>`;
}

/** Walk the TipTap doc back into raw `{value}` instructions text. */
export function agentInstructionsFromEditor(editor: Editor): string {
  const paragraphs: string[] = [];

  editor.state.doc.forEach((blockNode) => {
    let blockText = '';
    blockNode.forEach((child) => {
      if (child.type.name === 'mention') {
        blockText += `{${child.attrs.id}}`;
      } else if (child.type.name === 'hardBreak') {
        blockText += '\n';
      } else if (child.isText) {
        blockText += child.text ?? '';
      }
    });
    paragraphs.push(blockText);
  });

  return paragraphs.join('\n');
}
